import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import httpClient from "../httpClient";
import Swal from "sweetalert2";

export const useUserSignUp = () => {
  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async (payload) => {
      const response = await httpClient.post("/user/signup", payload);
      return response.data;
    },
    onSuccess: (response) => {
      Swal.fire({
        icon: "success",
        title: "Sign Up Successful",
        text: response?.message || "Your account has been created!",
        confirmButtonColor: "#3085d6",
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: "error",
        title: "Sign Up Failed",
        text: error?.response?.data?.error || "Something went wrong.",
      });
    },
  });

  return { mutate, isLoading, data, error };
};

export const useUserSignIn = () => {
  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async (payload) => {
      const response = await httpClient.post("/user/login", payload);
      return response.data;
    },
    onSuccess: (response) => {
      Swal.fire({
        icon: "success",
        title: "Welcome Back!",
        text: response?.message || "Login successful!",
        timer: 2000,
        showConfirmButton: false,
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: error?.response?.data?.error || "Invalid credentials.",
      });
    },
  });

  return { mutate, isLoading, data, error };
};

export const useUserInfo = () => {
  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async (payload) => {
      const response = await httpClient.post(`/users/user`, payload);
      return response.data;
    },
  });

  return { mutate, isLoading, data, error };
};

export const useGetUserData = () => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["userData"],
    queryFn: () => httpClient.get(`/user/getuser`),
  });

  return { data, error, isLoading };
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async (payload) => {
      const response = await httpClient.post("project/createProject", payload);
      return response.data;
    },
    onSuccess: (_, variables, response) => {
      queryClient.invalidateQueries(["projectList", variables.shop]);
      Swal.fire({
        icon: "success",
        title: "created project successfully",
        text: response?.message || "",
        timer: 2000,
        showConfirmButton: false,
      });
    },
  });

  return { mutate, isLoading, data, error };
};

export const useGetProjectList = () => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["projectList"],
    queryFn: () => httpClient.get(`/project/getProjects`),
  });

  return { data, error, isLoading };
};

export const useAddShop = () => {
  const queryClient = useQueryClient();

  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async (payload) => {
      const response = await httpClient.post(`/project/addShop`, payload);
      return response.data;
    },
    onSuccess: (_, variables, response) => {
      queryClient.invalidateQueries(["projectList", variables.shop]);
      Swal.fire({
        icon: "success",
        title: "Added shop successfully",
        text: response?.message || "",
        timer: 2000,
        showConfirmButton: false,
      });
    },
  });
  return { mutate, isLoading, data, error };
};

export const useGetValidate = (linkToken) => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["validateToken"],
    queryFn: () => httpClient.get(`/link/validate?linkToken=${linkToken}`),
    enabled: !!linkToken,
  });

  return { data, error, isLoading };
};

export const useGetShowcase = (projectId) => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["showcaseList"],
    queryFn: () =>
      httpClient.get(`/linkage/get_atttic_showcases?project_id=${projectId}`),
    enabled: !!projectId,
  });

  return { data, error, isLoading };
};

export const useGetShopifyProductVariant = (shopName) => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["shopInfo"],
    queryFn: () =>
      httpClient.get(`/linkage/get_shopify_products?shop=${shopName}`),
    enabled: !!shopName,
  });

  return { data, error, isLoading };
};

export const useGetAttticProduct = (projectId) => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["attticProduct"],
    queryFn: () =>
      httpClient.get(`/linkage/get_atttic_products?project_id=${projectId}`),
    enabled: !!projectId,
  });

  return { data, error, isLoading };
};

export const useGetAllLinkage = (shopName) => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["allLinkage", shopName],
    queryFn: () => httpClient.get(`/linkage/get_all_linkages?shop=${shopName}`),
    enabled: !!shopName,
  });

  return { data, error, isLoading };
};

export const useAddLinkage = () => {
  const queryClient = useQueryClient();

  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async (payload) => {
      const response = await httpClient.post(`/linkage/add_linkage`, payload);

      return response.data;
    },

    onSuccess: (_, variables, response) => {
      queryClient.invalidateQueries(["linkages", variables.shop]);
      Swal.fire({
        icon: "success",
        title: "Added linked successfully",
        text: response?.message || "The product is linked",
        timer: 2000,
        showConfirmButton: false,
      });
    },

    onError: (error) => {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.error || "Bad request.",
      });
    },
  });

  return { mutate, isLoading, data, error };
};

export const useGetShowcaseData = (projectId) => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["showcaseList"],
    queryFn: () =>
      httpClient.get(`/linkage/get_atttic_showcases?project_id=${projectId}`),
    enabled: !!projectId,
  });

  return { data, error, isLoading };
};

export const useSyncLinkageById = () => {
  const queryClient = useQueryClient();

  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async (payload) => {
      const response = await httpClient.post(`/linkage/sync`, payload);
      return response.data;
    },
    onSuccess: (_, variables, response) => {
      queryClient.invalidateQueries(["allLinkage"]);
      Swal.fire({
        icon: "success",
        title: "Product sync successfully",
        text: response?.message || "",
        timer: 2000,
        showConfirmButton: false,
      });
    },

    onError: (error) => {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.error || "Bad request.",
      });
    },
  });

  return { mutate, isLoading, data, error };
};

export const useSyncConfiguratorLinkageById = () => {
  const queryClient = useQueryClient();

  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async (payload) => {
      const response = await httpClient.post(
        `/configurator/sync_configurator`,
        payload
      );
      return response.data;
    },
    onSuccess: (_, variables, response) => {
      queryClient.invalidateQueries(["allLinkage"]);
      Swal.fire({
        icon: "success",
        title: "Product sync successfully",
        text: response?.message || "",
        timer: 2000,
        showConfirmButton: false,
      });
    },

    onError: (error) => {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.error || "Bad request.",
      });
    },
  });

  return { mutate, isLoading, data, error };
};

export const useGetConfigCollectionData = () => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["ConfigCollectionList"],
    queryFn: () => httpClient.get(`/configurator/get_collections_list`),
  });

  return { data, error, isLoading };
};

export const useLinkConfigurator = () => {
  const queryClient = useQueryClient();

  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async (payload) => {
      const response = await httpClient.post(
        `/configurator/link_configurator`,
        payload
      );
      return response.data;
    },
    onSuccess: (_, variables, response) => {
      queryClient.invalidateQueries(["allLinkage"]);
      Swal.fire({
        icon: "success",
        title: "Product linked successfully",
        text: response?.message || "",
        timer: 2000,
        showConfirmButton: false,
      });
    },

    onError: (error) => {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.error || "Bad request.",
      });
    },
  });

  return { mutate, isLoading, data, error };
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async (id) => {
      const response = await httpClient.delete(
        `/project/deleteProject?id=${id}`
      );
      return response.data;
    },

    onSuccess: (response) => {
      queryClient.invalidateQueries(["projectList"]);

      Swal.fire({
        icon: "success",
        title: "Project deleted successfully",
        text: response?.message || "",
        timer: 2000,
        showConfirmButton: false,
      });
    },

    onError: (error) => {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.error || "Something went wrong",
      });
    },
  });

  return { mutate, isLoading, data, error };
};

export const useModifyProject = () => {
  const queryClient = useQueryClient();

  const { mutate, isLoading, data, error } = useMutation({
    mutationFn: async ({ id, payload }) => {
      const response = await httpClient.put(
        `/project/updateProject?id=${id}`,
        payload
      );
      return response.data;
    },

    onSuccess: (response) => {
      queryClient.invalidateQueries(["projectList"]);

      Swal.fire({
        icon: "success",
        title: "Project updated successfully",
        text: response?.message || "",
        timer: 2000,
        showConfirmButton: false,
      });
    },

    onError: (error) => {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.error || "Something went wrong",
      });
    },
  });

  return { mutate, isLoading, data, error };
};
